import type { UploadedFile } from '@engagement-web-app/common';
import { createFileRoute, type ErrorComponentProps, Navigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useClientAuthentication } from '@/auth/ClientAuthenticationProvider';
import { FileListTable, FileListTableSkeleton } from '@/components/FileListTable';
import { Page } from '@/components/Page';
import { Pill } from '@/components/Pill';
import { RouteError } from '@/components/RouteError';
import { Skeleton } from '@/components/Skeleton';
import { showErrorToast } from '@/components/Toasts';
import { hasClientPermission } from '@/permissions/clientUserPermissions';
import { fetchUserFileDownloadUrl, fetchUserFiles } from '@/queries/user-files';
import * as styles from './index.css';

export const Route = createFileRoute('/_client/_auth/reports/')({
    component: ReportListRoute,
    loader: () => fetchUserFiles({ from: 0 }),
    pendingComponent: ReportListRouteSkeleton,
    errorComponent: ReportListRouteError,
});

function ReportListRouteError({ reset }: ErrorComponentProps) {
    return <RouteError kind="client" title="集計レポート" onRetry={reset} />;
}

function ReportListRouteSkeleton() {
    return (
        <Page.Root>
            {/* The title is static, so it renders for real rather than as a placeholder. */}
            <Page.Header title="集計レポート">
                <Skeleton.Line width={50} height="xl" />
            </Page.Header>

            <Page.Content>
                <section className={styles.section}>
                    <FileListTableSkeleton />
                </section>
            </Page.Content>
        </Page.Root>
    );
}

// Reports may contain company-wide aggregate data, so they are admin-only.
function ReportListRoute() {
    const currentUser = useClientAuthentication();

    if (!hasClientPermission(currentUser.role.id, 'reports', 'view')) {
        return <Navigate to="/" />;
    }

    return <ReportList />;
}

function ReportList() {
    const files = Route.useLoaderData();

    const handleDownload = useCallback(async (file: UploadedFile) => {
        try {
            const { downloadUrl } = await fetchUserFileDownloadUrl(file.fileId);
            window.open(downloadUrl, '_blank');
        } catch (error) {
            console.error('Failed to download file:', error);
            showErrorToast({
                title: 'ファイルのダウンロード',
                description: 'ファイルのダウンロードに失敗しました。',
            });
        }
    }, []);

    return (
        <Page.Root>
            <Page.Header title="集計レポート">
                <Pill>{`${files.total}件`}</Pill>
            </Page.Header>

            <Page.Content>
                {/* TODO: Extend shared Section component to allow no paddings and use it here */}
                <section className={styles.section}>
                    <FileListTable files={files.list} onDownload={handleDownload} showDeleteButton={false} />
                </section>
            </Page.Content>
        </Page.Root>
    );
}
